import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, useReducer } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { setNumeralLang } from "@/lib/numerals";

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "gu", label: "Gujarati", native: "ગુજરાતી" },
  { code: "mr", label: "Marathi", native: "मराठी" },
];

// Curated, human-style translations for core UI (nav, headers, common actions).
// Data values (names, SKUs, numbers) are never translated. Acronyms (BOM, OSP) stay as-is.
const TRANSLATIONS = {
  en: {
    "nav.dashboard": "Dashboard", "nav.schedule": "Schedule", "nav.myStages": "My Stages",
    "nav.bom": "BOM", "nav.lines": "Lines", "nav.purchasing": "Purchasing", "nav.suppliers": "Suppliers",
    "nav.customers": "Customers", "nav.sales": "Sales", "nav.osp": "OSP", "nav.inventory": "Inventory",
    "nav.planning": "Planning", "nav.systemMap": "System Map", "nav.users": "Users",
    "nav.workOrders": "Work Orders",
    "nav.approvals": "Approvals",
    "nav.myApprovals": "My Approvals",
    "nav.updates": "Updates",
    "nav.group.overview": "Overview", "nav.group.operations": "Operations",
    "nav.group.manufacturing": "Manufacturing", "nav.group.supplyChain": "Supply Chain",
    "nav.group.partners": "Partners", "nav.group.admin": "Admin",
    "nav.collapseSidebar": "Collapse", "nav.expandSidebar": "Expand", "nav.menu": "Menu",
    "nav.pinSidebar": "Pin open", "nav.unpinSidebar": "Unpin (auto-hide)",
    "nav.help": "Help", "nav.live": "Live", "nav.offline": "Offline", "nav.logout": "Log out",
    "header.title": "Operations Overview",
    "header.subtitle": "Real-time production planning & capacity control",
    "header.plant": "Plant",
    "btn.plantSettings": "Plant Settings", "btn.emailDigest": "Email Digest",
    "btn.purchaseList": "Purchase List", "btn.newOrder": "New Order",
    "common.save": "Save", "common.cancel": "Cancel", "common.confirm": "Confirm",
    "common.fulfil": "Fulfil", "common.edit": "Edit", "common.delete": "Delete", "common.close": "Close",
    "common.active": "Active", "common.inactive": "Inactive", "common.total": "total", "common.status": "Status", "common.actions": "Actions", "common.contact": "Contact", "common.items": "Items",
    "common.approve": "Approve", "common.reject": "Reject", "common.receive": "Receive", "common.history": "History", "common.resubmit": "Resubmit", "common.searchPo": "Search PO or supplier", "common.page": "Page", "common.of": "of",
    "col.customer": "Customer", "col.supplier": "Supplier", "col.categories": "Categories", "col.lead": "Lead", "col.terms": "Terms", "col.credit": "Credit", "col.reliability": "Reliability", "col.expected": "Expected", "col.total": "Total", "col.audit": "Audit", "col.po": "PO", "col.product": "Product", "col.qty": "Qty", "col.progress": "Progress", "col.priority": "Priority", "col.line": "Line", "col.stage": "Stage", "col.orderId": "Order ID", "col.due": "Due / Est. Finish",
    "customers.title": "Customer Master", "customers.add": "Add Customer", "customers.empty": "No customers yet.",
    "suppliers.title": "Supplier Master", "suppliers.add": "Add Supplier", "suppliers.empty": "No suppliers yet.", "suppliers.raisePo": "Raise PO",
    "po.titleMine": "My Purchase Orders", "po.titleAll": "Purchase Orders", "po.empty": "No purchase orders to display.",
    "po.new": "New Purchase Order", "po.newTitle": "New Purchase Order",
    "po.newDesc": "Pick a supplier and add raw materials or Brought Out parts with quantities.",
    "po.newDescAdmin": "Admin-raised POs are released immediately.",
    "po.newDescApproval": "Your PO starts as Pending Approval until an admin releases it.",
    "po.selectSupplier": "Select supplier…", "po.pickItem": "Pick a material…",
    "po.addItem": "Add item", "po.leadTime": "Lead time (days)", "po.raise": "Raise PO",
    "col.qty": "Qty",
    "suppliers.reliability": "Supplier Reliability", "prod.unassigned": "Unassigned",
    "prod.health": "Production Health",
    "language.label": "Language",
  },
  hi: {
    "nav.dashboard": "डैशबोर्ड", "nav.schedule": "शेड्यूल", "nav.myStages": "मेरे चरण",
    "nav.bom": "BOM", "nav.lines": "लाइनें", "nav.purchasing": "खरीद", "nav.suppliers": "आपूर्तिकर्ता",
    "nav.customers": "ग्राहक", "nav.sales": "बिक्री", "nav.osp": "OSP", "nav.inventory": "इन्वेंटरी",
    "nav.planning": "योजना", "nav.systemMap": "सिस्टम मैप", "nav.users": "उपयोगकर्ता",
    "nav.workOrders": "वर्क ऑर्डर",
    "nav.approvals": "अनुमोदन",
    "nav.myApprovals": "मेरे अनुमोदन",
    "nav.updates": "अपडेट",
    "nav.group.overview": "अवलोकन", "nav.group.operations": "परिचालन",
    "nav.group.manufacturing": "विनिर्माण", "nav.group.supplyChain": "आपूर्ति श्रृंखला",
    "nav.group.partners": "भागीदार", "nav.group.admin": "एडमिन",
    "nav.collapseSidebar": "समेटें", "nav.expandSidebar": "फैलाएँ", "nav.menu": "मेनू",
    "nav.pinSidebar": "पिन करें", "nav.unpinSidebar": "पिन हटाएँ (ऑटो-हाइड)",
    "nav.help": "मदद", "nav.live": "लाइव", "nav.offline": "ऑफ़लाइन", "nav.logout": "लॉग आउट",
    "header.title": "परिचालन अवलोकन",
    "header.subtitle": "रीयल-टाइम उत्पादन योजना और क्षमता नियंत्रण",
    "header.plant": "प्लांट",
    "btn.plantSettings": "प्लांट सेटिंग्स", "btn.emailDigest": "ईमेल डाइजेस्ट",
    "btn.purchaseList": "खरीद सूची", "btn.newOrder": "नया ऑर्डर",
    "common.save": "सहेजें", "common.cancel": "रद्द करें", "common.confirm": "पुष्टि करें",
    "common.fulfil": "पूरा करें", "common.edit": "संपादित करें", "common.delete": "हटाएँ", "common.close": "बंद करें",
    "common.active": "सक्रिय", "common.inactive": "निष्क्रिय", "common.total": "कुल", "common.status": "स्थिति", "common.actions": "क्रियाएँ", "common.contact": "संपर्क", "common.items": "आइटम",
    "common.approve": "स्वीकृत करें", "common.reject": "अस्वीकार करें", "common.receive": "प्राप्त करें", "common.history": "इतिहास", "common.resubmit": "पुनः सबमिट करें", "common.searchPo": "PO या आपूर्तिकर्ता खोजें", "common.page": "पेज", "common.of": "में से",
    "col.customer": "ग्राहक", "col.supplier": "आपूर्तिकर्ता", "col.categories": "श्रेणियाँ", "col.lead": "लीड", "col.terms": "शर्तें", "col.credit": "क्रेडिट", "col.reliability": "विश्वसनीयता", "col.expected": "अपेक्षित", "col.total": "कुल", "col.audit": "ऑडिट", "col.po": "PO", "col.product": "उत्पाद", "col.qty": "मात्रा", "col.progress": "प्रगति", "col.priority": "प्राथमिकता", "col.line": "लाइन", "col.stage": "चरण", "col.orderId": "ऑर्डर आईडी", "col.due": "नियत / अनुमानित समाप्ति",
    "customers.title": "ग्राहक मास्टर", "customers.add": "ग्राहक जोड़ें", "customers.empty": "अभी तक कोई ग्राहक नहीं।",
    "suppliers.title": "आपूर्तिकर्ता मास्टर", "suppliers.add": "आपूर्तिकर्ता जोड़ें", "suppliers.empty": "अभी तक कोई आपूर्तिकर्ता नहीं।", "suppliers.raisePo": "PO बनाएँ",
    "po.titleMine": "मेरे खरीद ऑर्डर", "po.titleAll": "खरीद ऑर्डर", "po.empty": "दिखाने के लिए कोई खरीद ऑर्डर नहीं।",
    "po.new": "नया खरीद ऑर्डर", "po.newTitle": "नया खरीद ऑर्डर",
    "po.newDesc": "आपूर्तिकर्ता चुनें और मात्रा सहित कच्चा माल या Brought Out पार्ट्स जोड़ें।",
    "po.newDescAdmin": "एडमिन द्वारा बनाए गए PO तुरंत रिलीज़ हो जाते हैं।",
    "po.newDescApproval": "आपका PO तब तक Pending Approval रहेगा जब तक कोई एडमिन इसे रिलीज़ न करे।",
    "po.selectSupplier": "आपूर्तिकर्ता चुनें…", "po.pickItem": "सामग्री चुनें…",
    "po.addItem": "आइटम जोड़ें", "po.leadTime": "लीड टाइम (दिन)", "po.raise": "PO बनाएं",
    "col.qty": "मात्रा",
    "suppliers.reliability": "आपूर्तिकर्ता विश्वसनीयता", "prod.unassigned": "असाइन नहीं किया गया",
    "prod.health": "उत्पादन स्वास्थ्य",
    "language.label": "भाषा",
  },
  gu: {
    "nav.dashboard": "ડેશબોર્ડ", "nav.schedule": "શેડ્યૂલ", "nav.myStages": "મારા તબક્કા",
    "nav.bom": "BOM", "nav.lines": "લાઇનો", "nav.purchasing": "ખરીદી", "nav.suppliers": "સપ્લાયર્સ",
    "nav.customers": "ગ્રાહકો", "nav.sales": "વેચાણ", "nav.osp": "OSP", "nav.inventory": "ઈન્વેન્ટરી",
    "nav.planning": "આયોજન", "nav.systemMap": "સિસ્ટમ મેપ", "nav.users": "વપરાશકર્તાઓ",
    "nav.workOrders": "વર્ક ઓર્ડર",
    "nav.approvals": "મંજૂરી",
    "nav.myApprovals": "મારી મંજૂરી",
    "nav.updates": "અપડેટ",
    "nav.group.overview": "ઓવરવ્યૂ", "nav.group.operations": "ઓપરેશન્સ",
    "nav.group.manufacturing": "ઉત્પાદન", "nav.group.supplyChain": "સપ્લાય ચેઇન",
    "nav.group.partners": "પાર્ટનર્સ", "nav.group.admin": "એડમિન",
    "nav.collapseSidebar": "સંકુચિત કરો", "nav.expandSidebar": "વિસ્તારો", "nav.menu": "મેનૂ",
    "nav.pinSidebar": "પિન કરો", "nav.unpinSidebar": "પિન હટાવો (ઓટો-હાઇડ)",
    "nav.help": "મદદ", "nav.live": "લાઇવ", "nav.offline": "ઑફલાઇન", "nav.logout": "લૉગ આઉટ",
    "header.title": "ઓપરેશન્સ ઓવરવ્યૂ",
    "header.subtitle": "રિયલ-ટાઇમ ઉત્પાદન આયોજન અને ક્ષમતા નિયંત્રણ",
    "header.plant": "પ્લાન્ટ",
    "btn.plantSettings": "પ્લાન્ટ સેટિંગ્સ", "btn.emailDigest": "ઈમેલ ડાયજેસ્ટ",
    "btn.purchaseList": "ખરીદ યાદી", "btn.newOrder": "નવો ઓર્ડર",
    "common.save": "સાચવો", "common.cancel": "રદ કરો", "common.confirm": "પુષ્ટિ કરો",
    "common.fulfil": "પૂર્ણ કરો", "common.edit": "સંપાદિત કરો", "common.delete": "કાઢી નાખો", "common.close": "બંધ કરો",
    "common.active": "સક્રિય", "common.inactive": "નિષ્ક્રિય", "common.total": "કુલ", "common.status": "સ્થિતિ", "common.actions": "ક્રિયાઓ", "common.contact": "સંપર્ક", "common.items": "આઇટમ",
    "common.approve": "મંજૂર કરો", "common.reject": "નકારો", "common.receive": "પ્રાપ્ત કરો", "common.history": "ઇતિહાસ", "common.resubmit": "ફરી સબમિટ કરો", "common.searchPo": "PO અથવા સપ્લાયર શોધો", "common.page": "પૃષ્ઠ", "common.of": "માંથી",
    "col.customer": "ગ્રાહક", "col.supplier": "સપ્લાયર", "col.categories": "શ્રેણીઓ", "col.lead": "લીડ", "col.terms": "શરતો", "col.credit": "ક્રેડિટ", "col.reliability": "વિશ્વસનીયતા", "col.expected": "અપેક્ષિત", "col.total": "કુલ", "col.audit": "ઓડિટ", "col.po": "PO", "col.product": "ઉત્પાદન", "col.qty": "જથ્થો", "col.progress": "પ્રગતિ", "col.priority": "પ્રાધાન્ય", "col.line": "લાઇન", "col.stage": "તબક્કો", "col.orderId": "ઓર્ડર આઈડી", "col.due": "નિયત / અંદાજિત પૂર્ણતા",
    "customers.title": "ગ્રાહક માસ્ટર", "customers.add": "ગ્રાહક ઉમેરો", "customers.empty": "હજી સુધી કોઈ ગ્રાહક નથી.",
    "suppliers.title": "સપ્લાયર માસ્ટર", "suppliers.add": "સપ્લાયર ઉમેરો", "suppliers.empty": "હજી સુધી કોઈ સપ્લાયર નથી.", "suppliers.raisePo": "PO બનાવો",
    "po.titleMine": "મારા ખરીદ ઓર્ડર", "po.titleAll": "ખરીદ ઓર્ડર", "po.empty": "બતાવવા માટે કોઈ ખરીદ ઓર્ડર નથી.",
    "po.new": "નવો ખરીદ ઓર્ડર", "po.newTitle": "નવો ખરીદ ઓર્ડર",
    "po.newDesc": "સપ્લાયર પસંદ કરો અને જથ્થા સાથે કાચો માલ અથવા Brought Out પાર્ટ્સ ઉમેરો.",
    "po.newDescAdmin": "એડમિન દ્વારા બનાવેલ PO તરત જ રિલીઝ થઈ જાય છે.",
    "po.newDescApproval": "એડમિન રિલીઝ ન કરે ત્યાં સુધી તમારો PO Pending Approval રહે છે.",
    "po.selectSupplier": "સપ્લાયર પસંદ કરો…", "po.pickItem": "મટીરિયલ પસંદ કરો…",
    "po.addItem": "આઇટમ ઉમેરો", "po.leadTime": "લીડ ટાઇમ (દિવસ)", "po.raise": "PO બનાવો",
    "col.qty": "જથ્થો",
    "suppliers.reliability": "સપ્લાયર વિશ્વસનીયતા", "prod.unassigned": "અસાઇન કરેલ નથી",
    "prod.health": "ઉત્પાદન આરોગ્ય",
    "language.label": "ભાષા",
  },
  mr: {
    "nav.dashboard": "डॅशबोर्ड", "nav.schedule": "वेळापत्रक", "nav.myStages": "माझे टप्पे",
    "nav.bom": "BOM", "nav.lines": "लाइन्स", "nav.purchasing": "खरेदी", "nav.suppliers": "पुरवठादार",
    "nav.customers": "ग्राहक", "nav.sales": "विक्री", "nav.osp": "OSP", "nav.inventory": "साठा",
    "nav.planning": "नियोजन", "nav.systemMap": "सिस्टम नकाशा", "nav.users": "वापरकर्ते",
    "nav.workOrders": "वर्क ऑर्डर",
    "nav.approvals": "मान्यता",
    "nav.myApprovals": "माझी मान्यता",
    "nav.updates": "अपडेट",
    "nav.group.overview": "आढावा", "nav.group.operations": "ऑपरेशन्स",
    "nav.group.manufacturing": "उत्पादन", "nav.group.supplyChain": "पुरवठा साखळी",
    "nav.group.partners": "भागीदार", "nav.group.admin": "ॲडमिन",
    "nav.collapseSidebar": "लहान करा", "nav.expandSidebar": "विस्तार करा", "nav.menu": "मेनू",
    "nav.pinSidebar": "पिन करा", "nav.unpinSidebar": "पिन काढा (ऑटो-हाइड)",
    "nav.help": "मदत", "nav.live": "लाइव्ह", "nav.offline": "ऑफलाइन", "nav.logout": "लॉग आउट",
    "header.title": "कामकाज आढावा",
    "header.subtitle": "रिअल-टाइम उत्पादन नियोजन आणि क्षमता नियंत्रण",
    "header.plant": "प्लांट",
    "btn.plantSettings": "प्लांट सेटिंग्ज", "btn.emailDigest": "ईमेल डायजेस्ट",
    "btn.purchaseList": "खरेदी यादी", "btn.newOrder": "नवीन ऑर्डर",
    "common.save": "जतन करा", "common.cancel": "रद्द करा", "common.confirm": "पुष्टी करा",
    "common.fulfil": "पूर्ण करा", "common.edit": "संपादित करा", "common.delete": "हटवा", "common.close": "बंद करा",
    "common.active": "सक्रिय", "common.inactive": "निष्क्रिय", "common.total": "एकूण", "common.status": "स्थिती", "common.actions": "क्रिया", "common.contact": "संपर्क", "common.items": "आयटम",
    "common.approve": "मंजूर करा", "common.reject": "नाकारा", "common.receive": "प्राप्त करा", "common.history": "इतिहास", "common.resubmit": "पुन्हा सबमिट करा", "common.searchPo": "PO किंवा पुरवठादार शोधा", "common.page": "पान", "common.of": "पैकी",
    "col.customer": "ग्राहक", "col.supplier": "पुरवठादार", "col.categories": "श्रेणी", "col.lead": "लीड", "col.terms": "अटी", "col.credit": "क्रेडिट", "col.reliability": "विश्वासार्हता", "col.expected": "अपेक्षित", "col.total": "एकूण", "col.audit": "ऑडिट", "col.po": "PO", "col.product": "उत्पादन", "col.qty": "प्रमाण", "col.progress": "प्रगती", "col.priority": "प्राधान्य", "col.line": "लाइन", "col.stage": "टप्पा", "col.orderId": "ऑर्डर आयडी", "col.due": "देय / अंदाजित पूर्तता",
    "customers.title": "ग्राहक मास्टर", "customers.add": "ग्राहक जोडा", "customers.empty": "अद्याप कोणतेही ग्राहक नाहीत.",
    "suppliers.title": "पुरवठादार मास्टर", "suppliers.add": "पुरवठादार जोडा", "suppliers.empty": "अद्याप कोणतेही पुरवठादार नाहीत.", "suppliers.raisePo": "PO तयार करा",
    "po.titleMine": "माझे खरेदी ऑर्डर", "po.titleAll": "खरेदी ऑर्डर", "po.empty": "दाखवण्यासाठी कोणतेही खरेदी ऑर्डर नाहीत.",
    "po.new": "नवीन खरेदी ऑर्डर", "po.newTitle": "नवीन खरेदी ऑर्डर",
    "po.newDesc": "पुरवठादार निवडा आणि प्रमाणासह कच्चा माल किंवा Brought Out पार्ट्स जोडा.",
    "po.newDescAdmin": "अॅडमिनने बनवलेले PO लगेच रिलीज होतात.",
    "po.newDescApproval": "अॅडमिन रिलीज करेपर्यंत तुमचा PO Pending Approval राहील.",
    "po.selectSupplier": "पुरवठादार निवडा…", "po.pickItem": "साहित्य निवडा…",
    "po.addItem": "आयटम जोडा", "po.leadTime": "लीड टाइम (दिवस)", "po.raise": "PO तयार करा",
    "col.qty": "नग",
    "suppliers.reliability": "पुरवठादार विश्वासार्हता", "prod.unassigned": "असाइन केलेले नाही",
    "prod.health": "उत्पादन आरोग्य",
    "language.label": "भाषा",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "en");

  // Data-name translation cache (LLM-backed). Keyed by `${lang}::${text}`.
  const cacheRef = useRef({});
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);
  const langRef = useRef(lang);
  const [cacheVersion, bump] = useReducer((x) => x + 1, 0);

  useEffect(() => { langRef.current = lang; setNumeralLang(lang); }, [lang]);
  useEffect(() => { setNumeralLang(lang); }, []); // ensure digits correct on first paint

  // On first login in a fresh browser (no local choice), adopt the user's saved preference.
  useEffect(() => {
    if (user && typeof user === "object" && user.preferred_language && !localStorage.getItem("lang")) {
      setLangState(user.preferred_language);
    }
  }, [user]);

  const setLang = useCallback((code) => {
    setLangState(code);
    setNumeralLang(code);
    localStorage.setItem("lang", code);
    api.post("/auth/me/language", { language: code }).catch(() => {}); // best-effort persist
  }, []);

  const t = useCallback(
    (key) => (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) || TRANSLATIONS.en[key] || key,
    [lang],
  );

  const flush = useCallback(async () => {
    const texts = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    const curLang = langRef.current;
    if (curLang === "en" || texts.length === 0) return;
    try {
      const res = await api.post("/i18n/translate", { texts, lang: curLang });
      const map = (res && res.translations) || {};
      const manualSet = new Set((res && res.manual) || []);
      Object.entries(map).forEach(([src, dst]) => { cacheRef.current[`${curLang}::${src}`] = { v: dst, m: manualSet.has(src) }; });
      bump();
    } catch (err) {
      logger.error("Name translation request failed; keeping originals:", err);
    }
  }, []);

  // Translate a data value (product/customer/supplier name). Returns the original text until
  // the async translation arrives, then re-renders. English + codes/SKUs pass through unchanged.
  // tnInfo also reports provenance: "machine" (LLM) vs "manual" (admin-corrected, locked).
  const tnInfo = useCallback((text) => {
    if (!text || lang === "en") return { text, state: "original" };
    const hit = cacheRef.current[`${lang}::${text}`];
    if (hit !== undefined) return { text: hit.v, state: hit.m ? "manual" : "machine" };
    pendingRef.current.add(text);
    if (!timerRef.current) {
      timerRef.current = setTimeout(() => { timerRef.current = null; flush(); }, 200);
    }
    return { text, state: "pending" };
  }, [lang, flush]);

  const tn = useCallback((text) => tnInfo(text).text, [tnInfo]);

  // Apply an admin correction instantly to the local cache (already persisted server-side).
  const applyOverride = useCallback((text, dst) => {
    cacheRef.current[`${langRef.current}::${text}`] = { v: dst, m: true };
    bump();
  }, []);

  // cacheVersion in deps: flushes/overrides must change context identity or consumers never re-render.
  const value = useMemo(() => ({ lang, setLang, t, tn, tnInfo, applyOverride, languages: LANGUAGES, cacheVersion }), [lang, setLang, t, tn, tnInfo, applyOverride, cacheVersion]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;
export const useTn = () => useContext(I18nContext).tn;
